import { useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { classes, stylesheet } from 'typestyle'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'

/* Components =================================================================================== */
import AppLayout, { MAX_WIDTH } from './layout'
import Footer from '../__home__/components/footer'
import Skeleton from '../../__aeki__/components/skeleton'
import { A } from '../../__aeki__/components/a'
import { Box } from '../../__aeki__/components/box/box.component'
import { Info } from '../posts/components/info'
import { Line } from '../../__aeki__/components/line'
import { Loader } from '../../__aeki__/components/loader'
import { Search } from '../../__aeki__/components/search/search.component'
import { SimplerPagination } from '../../__aeki__/modules/navigation/pagination'
import { Text } from '../../__aeki__/components/text/text.component'
import { Title } from '../../__aeki__/components/text/title.component'
import { VisibleFade } from '../__home__/components/visible/visible-fade'
import { VisibleOpacity } from '../__home__/components/visibile'

/* Styles ======================================================================================= */
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { mb24 } from '../../__aeki__/styles/styleset/margin/mb24'
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { mt52 } from '../../__aeki__/styles/styleset/margin/mt52'
import { paddingVerticalOuter } from '../../__aeki__/styles/default/padding'
import { pt40 } from '../../__aeki__/styles/styleset/padding/pt40'
import { textCenter } from '../../__aeki__/styles/styleset/text-center/textCenter'
import { w100smi } from '../../__aeki__/styles/styleset/width/w100smi'
import { w80 } from '../../__aeki__/styles/styleset/width/w80'
import { withRouter } from '../../__aeki__/contexts/with-router'

const Neighbourhoods: any = withRouter((props: any) => {
  const { inject, eject } = props
  const injection = inject()
  const history = useNavigate()
  const historyLocation = useLocation()

  const [contents, $contents] = useFetch<any>(
    Object.keys(injection).length
      ? injection
      : {
          data: [],
        },
    {
      search: {
        page: 1,
      },
      url: `${process.env.REACT_APP_API_URL}/a/places`,
      location: props.history ? props.historyLocation : undefined,
      onLocationChange: (params: any) => {
        const { encodedParam } = params
        window.history.replaceState({}, '', `?${encodedParam}`)
      },
    },
  )

  useEffect(() => {
    return () => {
      eject()
    }
  }, [])

  useEffect(() => {
    if (contents.mounted && Object.keys(injection).length === 0) {
      $contents.get({
        search: {
          page: contents.search.page,
        },
      })
    }
  }, [contents.mounted])

  const getHeadElements = () => {
    const page = contents.payload.page
    const totalPage = contents.payload.totalPage
    const getPaginatedUrl = (newPage: any) => {
      const url = `${process.env.REACT_APP_WEB_URL}${historyLocation.pathname}${historyLocation.search}`.replace(
        'childcare-not-found',
        'childcares',
      )

      if (newPage !== undefined && url.includes('page=')) {
        return url.replace(`page=${page}`, `page=${newPage}`)
      } else {
        if (url.includes('?')) {
          return `${url}&page=${newPage}`
        } else {
          return `${url}?page=${newPage}`
        }
      }
    }

    return {
      canonical: `${process.env.REACT_APP_WEB_URL}${historyLocation.pathname}`,
      url: `${process.env.REACT_APP_WEB_URL}${historyLocation.pathname}`,
      prevPage: page > 1 && getPaginatedUrl(page - 1),
      nextPage: page < totalPage && getPaginatedUrl(page + 1),
    }
  }

  const content = useMemo(() => {
    return (
      <VisibleFade>
        {contents.payload?.data?.map((place: any, key: any) => {
          return (
            <Box className={mb24} key={key}>
              <Link to={`/a/childcares/${place.body.key}`}>
                <Box row>
                  <Text size="lg" className={mb8}>
                    {place.body.name}
                  </Text>
                  <Line />
                </Box>
              </Link>
            </Box>
          )
        })}
      </VisibleFade>
    )
  }, [contents.payload?.data?.length > 0, contents.search])

  return (
    <AppLayout.render
      head={{
        title: 'Browse All Places - ChildAt.io',
        description: 'Browse all the cities and neighbourhoods we cover in Canada',
        ...getHeadElements(),
      }}
      headerProps={{
        maxWidth: MAX_WIDTH,
        scrollEffect: true,
        allowToggleEffect: true,
      }}
      content={
        <>
          <Box baseline="secondary" className={STYLES.containerHero} row>
            <Box
              baseline="none"
              className={STYLES.containerHeroContent}
              flex
              alignCenterX
              alignCenterY
            >
              <Box
                className={classes(paddingVerticalOuter)}
                baseline="none"
                maxWidth={MAX_WIDTH}
                centerAutoX
                fullWidth
                row
              >
                <VisibleOpacity>
                  <Box row baseline="none" alignCenterX>
                    <Link to={'/a/blog'}>
                      <Text
                        baseline="primary"
                        className={classes(mb8, textCenter)}
                        size="lg"
                        tag={'h2'}
                      >
                        All the cities and neighbourhoods in Canada
                      </Text>
                    </Link>
                    <Title className={textCenter} size="md" semiBold tag={'h1'} crank>
                      Browse all places
                    </Title>
                  </Box>
                </VisibleOpacity>
              </Box>
            </Box>
          </Box>
          <Info maxWidth={MAX_WIDTH} label="Subscribe" labelWidth={80} icon="error">
            <Text size="sm">
              Sign up with us so you can get notified of our posts by clicking{' '}
              <A to={'/a/signin'}>
                <Text size="sm" link>
                  here!
                </Text>
              </A>
            </Text>
          </Info>
          <Box className={classes(pt40)} row>
            <Box className={paddingVerticalOuter} maxWidth={MAX_WIDTH} centerAutoX row fullWidth>
              <Box baseline="background" className={mb24} rounded>
                <Search
                  onChange={(value: any) => {
                    if ($contents.get) {
                      if (value) {
                        $contents?.get?.({
                          search: {
                            page: 1,
                            searchKeyword: value,
                          },
                        })
                      } else {
                        $contents?.get?.({
                          search: {
                            page: 1,
                            searchKeyword: '',
                          },
                        })
                      }
                    }
                  }}
                />
              </Box>
              <Loader
                timeout={0}
                loaded={contents.payload?.data?.length > 0}
                skeleton={
                  <>
                    <Skeleton className={classes(w100smi, w80, mb8)} height={34} />
                  </>
                }
                component={
                  <>
                    {content}
                    <Box alignCenterX>
                      <SimplerPagination
                        data={contents}
                        $data={$contents}
                        isLink={true}
                        url={
                          globalThis.window &&
                          `${globalThis.window.location.pathname}${globalThis.window.location.search}`
                        }
                      />
                    </Box>
                  </>
                }
              />
            </Box>
            <Line className={mt52} />
            <Box className={classes(pt40)} baseline="background">
              <Footer />
            </Box>
          </Box>
        </>
      }
    />
  )
})

/* Styles ======================================================================================= */
const STYLES = stylesheet({
  containerHero: {
    marginTop: -56,
    paddingTop: 56,
  },
  containerHeroContent: {
    height: 'calc(40vh)',
    maxHeight: 600,
    ...createMediaQuery.sm({
      height: 'calc(100vh - 97px)',
    }),
  },
  lineHero: {
    opacity: 0.1,
  },

  container: {
    width: '100%',
  },
  containerInner: {
    margin: '0 auto',
    maxWidth: MAX_WIDTH,
  },
  containerFooter: {
    padding: '40px 12px 12px 12px',
  },
})

export default Neighbourhoods
